import { useCallback } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { CampaignSearch, OutreachCrmTab, OutreachCrmView } from './search';

export const CRM_DEFAULT_TAB: OutreachCrmTab = 'overview';
export const CRM_DEFAULT_VIEW: OutreachCrmView = 'all';

/** The URL keys the outreach CRM owns; every way out of the CRM clears them. */
export const CRM_SEARCH_KEYS = ['crm_tab', 'crm_view', 'crm_q'] as const;

export const CLEARED_CRM_SEARCH: Pick<Partial<CampaignSearch>, (typeof CRM_SEARCH_KEYS)[number]> = {
  crm_tab: undefined,
  crm_view: undefined,
  crm_q: undefined,
};

/**
 * The CRM's tab, Creators list view and search, URL-owned so a refresh or a
 * shared link reopens the same view. Defaults are written as `undefined` to
 * keep the common URL clean, and every change resets `page`: a page number
 * from one list means nothing in another.
 */
export function useCrmViewParam() {
  const { crm_tab, crm_view, crm_q } = useSearch({ strict: false }) as Partial<CampaignSearch>;
  const navigate = useNavigate();

  const update = useCallback(
    (patch: Partial<CampaignSearch>) =>
      navigate({
        to: '.',
        search: (prev: Partial<CampaignSearch>) => ({ ...prev, ...patch, page: undefined }),
        resetScroll: false,
      } as never),
    [navigate],
  );

  const setTab = useCallback(
    (next: OutreachCrmTab) => update({ crm_tab: next === CRM_DEFAULT_TAB ? undefined : next }),
    [update],
  );
  const setView = useCallback(
    (next: OutreachCrmView) => update({ crm_view: next === CRM_DEFAULT_VIEW ? undefined : next }),
    [update],
  );
  const setQuery = useCallback(
    (next: string) => {
      const trimmed = next.trim();
      update({ crm_q: trimmed === '' ? undefined : trimmed });
    },
    [update],
  );

  return {
    tab: crm_tab ?? CRM_DEFAULT_TAB,
    view: crm_view ?? CRM_DEFAULT_VIEW,
    query: crm_q ?? '',
    setTab,
    setView,
    setQuery,
  };
}
